import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Send, Plus, Edit, Trash2, Search, Calendar, Users, MessageSquare, X, CheckCircle, BarChart3, FileText, LayoutGrid, List, Download, Filter } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import CampaignAnalytics from '../components/CampaignAnalytics';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useConfirm } from '../hooks/useConfirm';
import { campaignsAPI, groupsAPI, contactsAPI, fluxosAPI, templatesMetaAPI, instancesAPI } from '../services/api';
import { isoParaDatetimeLocal, datetimeLocalParaISO, formatarData, formatarDataHora } from '../utils/dateFormatter';
import { PageSkeleton, SkeletonTabela } from '../components/Skeleton';

const Campaigns = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showConfirm = useConfirm();
  const [campaigns, setCampaigns] = useState([]);
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('busca') || "");
  const filtroInstancia = searchParams.get('instancia') || "";
  const filtroTipo = searchParams.get('tipo') || "";
  const filtroStatus = searchParams.get('status') || "";
  const hoje = new Date().toISOString().slice(0, 10);
  const seteDiasAtras = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const filtroDe = searchParams.get('de') || seteDiasAtras;
  const filtroAte = searchParams.get('ate') || hoje;
  const [showAnalytics, setShowAnalytics] = useState(() => localStorage.getItem('campaigns_showAnalytics') !== 'false');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('campaigns_viewMode') || 'kanban');
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    tipo: "manual",
    scheduled_date: "",
    target_type: "grupo",
    target_id: "",
    status: true,
    fluxo_id: "",
    tempo_expiracao_fluxo: 24,
    template_id: "",
    instancia_id: "",
  });

  const { user, isOperador, isGerente } = useAuth();
  const { darkMode } = useTheme();
  const [fluxos, setFluxos] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  const updateFiltro = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (!user?.cliente_id) return;
    loadCampaigns();
    loadGroups();
    loadFluxos();
    loadContacts();
    loadTemplates();
    loadInstances();
  }, [user?.cliente_id]);

  // Recarregar quando filtros da URL mudam
  useEffect(() => {
    if (!user?.cliente_id) return;
    loadCampaigns();
  }, []);

  // Polling: atualizar Kanban a cada 5s se tem campanha executando
  useEffect(() => {
    const temExecutando = campaigns.some(c => c.status_execucao === 'executando');
    if (!temExecutando) return;
    const interval = setInterval(loadCampaigns, 5000);
    return () => clearInterval(interval);
  }, [campaigns]);

  const loadCampaigns = async () => {
    try {
      const params = {};
      if (filtroDe) params.de = filtroDe;
      if (filtroAte) params.ate = filtroAte;
      if (filtroInstancia) params.instancia_id = filtroInstancia;
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroStatus) params.status = filtroStatus;
      const response = await campaignsAPI.list(params);
      setCampaigns(response.data?.campanhas || response.data || []);
    } catch (error) {
      toast.error("Erro ao carregar campanhas");
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await groupsAPI.list(user.cliente_id);
      setGroups(response.data?.grupos || response.data || []);
    } catch (error) {
      console.error("Erro ao carregar grupos:", error);
      setGroups([]);
    }
  };

  const loadFluxos = async () => {
    try {
      const response = await fluxosAPI.list(user.cliente_id);
      // A API responde { success, data }: o `response.data || []` antigo pegava
      // o envelope, reprovava no Array.isArray e o select ficava sempre vazio.
      const data = response.data?.data || response.data || [];
      setFluxos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar fluxos:", error);
      setFluxos([]);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await contactsAPI.search(user.cliente_id);
      setContacts(response.data?.messages || response.data || []);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
      setContacts([]);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await templatesMetaAPI.list();
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar templates:", error);
      setTemplates([]);
    }
  };

  const loadInstances = async () => {
    try {
      const response = await instancesAPI.list(user.cliente_id);
      const conectadas = (response.data || []).filter(i => i.status === 'conectada' && i.platform === 'whatsapp');
      setInstances(conectadas);
      
      // Selecionar primeira instância por padrão
      if (conectadas.length > 0 && !formData.instancia_id) {
        setFormData(prev => ({ ...prev, instancia_id: conectadas[0].id }));
      }
    } catch (error) {
      console.error("Erro ao carregar instâncias:", error);
      setInstances([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const campaignData = {
        ...formData,
        cliente_id: user.cliente_id,
        usuario_id: user.id,
        scheduled_date: formData.scheduled_date
          ? datetimeLocalParaISO(formData.scheduled_date) // Converte para ISO UTC
          : null,
      };

      if (editingCampaign) {
        await campaignsAPI.update(editingCampaign.id, campaignData);
        toast.success("Campanha atualizada com sucesso!");
      } else {
        await campaignsAPI.create(campaignData);
        toast.success("Campanha criada com sucesso!");
      }

      setShowModal(false);
      setEditingCampaign(null);
      setFormData({
        nome: "",
        descricao: "",
        tipo: "manual",
        scheduled_date: "",
        target_type: "grupo",
        target_id: "",
        status: true,
        fluxo_id: "",
        tempo_expiracao_fluxo: 24,
        template_id: "",
      });
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao salvar campanha");
    }
  };

  const handleEdit = (campaign) => {
    navigate(`/marketing/campanhas/editar/${campaign.id}`);
  };

  const handleDelete = async (id) => {
    const campanha = campaigns.find(c => c.id === id);
    if (campanha?.status_execucao && ['completa', 'incompleta', 'erro', 'executando'].includes(campanha.status_execucao)) {
      toast.error('Não é possível excluir uma campanha que já foi executada');
      return;
    }
    showConfirm({
      title: 'Excluir Campanha',
      message: 'Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      type: 'danger',
      onConfirm: async () => {
        try {
          await campaignsAPI.delete(id);
          toast.success("Campanha excluída com sucesso!");
          loadCampaigns();
        } catch (error) {
          toast.error("Erro ao excluir campanha");
        }
      }
    });
  };

  const handleMarkReady = async (id) => {
    const campanha = campaigns.find(c => c.id === id);
    // Campanhas individuais com template precisam ter template aprovado
    if (campanha?.tipo_campanha !== 'grupo_whatsapp' && campanha?.template_status !== 'aprovado' && !campanha?.template_id) {
      toast.error("Template precisa estar aprovado pela Meta antes de marcar como pronta");
      return;
    }
    try {
      await campaignsAPI.update(id, { status_campanha: 'pronta' });
      toast.success("Campanha marcada como pronta!");
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao atualizar campanha");
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case "agendada":
        return <Calendar className="h-4 w-4" />;
      case "por_acao":
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Send className="h-4 w-4" />;
    }
  };

  const filteredCampaigns = Array.isArray(campaigns)
    ? campaigns.filter((campaign) =>
        campaign?.nome?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const exportCSV = () => {
    if (!filteredCampaigns.length) return toast.error('Nenhuma campanha para exportar');
    const headers = ['Nome', 'Status', 'Tipo', 'Enviados', 'Entregues', 'Lidos', 'Falhas', 'Criador', 'Data'];
    const rows = filteredCampaigns.map(c => [
      c.nome || '',
      c.status_execucao || c.status_campanha || 'rascunho',
      c.tipo_campanha === 'grupo_whatsapp' ? 'Grupos' : 'Contatos',
      c.total_enviados || 0,
      c.total_entregues || 0,
      c.total_lidos || 0,
      c.total_falhas || 0,
      c.usuario?.nome || '',
      c.create_at ? new Date(c.create_at).toLocaleDateString('pt-BR') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `campanhas_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('CSV exportado!');
  };

  if (loading) {
    return (
      <Layout expanded>
        <PageSkeleton title="Campanhas" subtitle="Crie e gerencie campanhas de marketing">
          <SkeletonTabela colunas={6} linhas={8} />
        </PageSkeleton>
      </Layout>
    );
  }

  return (
    <Layout expanded>
      <div className="space-y-6">
        <PageHeader title="Campanhas" subtitle="Crie e gerencie campanhas de marketing">
            <button
              onClick={() => { const v = !showAnalytics; setShowAnalytics(v); localStorage.setItem('campaigns_showAnalytics', v); }}
              className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border rounded-md text-sm font-medium ${
                darkMode
                  ? "border-gray-600 text-gray-300 hover:bg-zinc-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <BarChart3 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{showAnalytics ? 'Ocultar' : 'Mostrar'} Analytics</span>
            </button>
            {isOperador() && (
              <button
                onClick={() => navigate('/marketing/campanhas/nova')}
                className="gradient-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white bg-nomoz-orange hover:bg-nomoz-orange-dark"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nova Campanha</span>
              </button>
            )}
        </PageHeader>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <CampaignAnalytics campaigns={campaigns} darkMode={darkMode} />
        )}

        {/* Filtros + View Switcher */}
        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-wrap items-center gap-3 [&>select]:flex-1 [&>select]:min-w-[120px] [&>input]:flex-1 [&>input]:min-w-[120px]">
            <Filter className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />

            {/* Busca */}
            <div className="relative flex-[2] min-w-[250px]">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Buscar campanha..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); updateFiltro('busca', e.target.value); }}
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              />
            </div>

            {/* Status */}
            <select
              value={filtroStatus}
              onChange={(e) => updateFiltro('status', e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">Todos status</option>
              <option value="rascunho">Rascunho</option>
              <option value="pronta">Pronta</option>
              <option value="executando">Executando</option>
              <option value="completa">Concluída</option>
              <option value="erro">Erro</option>
            </select>

            {/* Instância */}
            <select
              value={filtroInstancia}
              onChange={(e) => updateFiltro('instancia', e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">Todas instâncias</option>
              {instances.map(inst => (
                <option key={inst.id} value={inst.id}>{inst.nome_instancia}</option>
              ))}
            </select>

            {/* Tipo */}
            <select
              value={filtroTipo}
              onChange={(e) => updateFiltro('tipo', e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">Todos tipos</option>
              <option value="grupo">Grupos</option>
              <option value="contato">Contatos</option>
            </select>

            {/* Data De */}
            <input
              type="date"
              value={filtroDe}
              onChange={(e) => updateFiltro('de', e.target.value)}
              className={`px-2 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              title="Data início"
            />

            {/* Data Até */}
            <input
              type="date"
              value={filtroAte}
              onChange={(e) => updateFiltro('ate', e.target.value)}
              className={`px-2 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              title="Data fim"
            />

            {/* Buscar */}
            <button onClick={loadCampaigns}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-nomoz-orange text-white hover:bg-nomoz-orange-dark whitespace-nowrap"
            >
              Buscar
            </button>

            {/* Limpar */}
            {(searchTerm || filtroInstancia || filtroTipo || filtroStatus || filtroDe || filtroAte) && (
              <button
                onClick={() => { setSearchTerm(''); setSearchParams({}, { replace: true }); setTimeout(loadCampaigns, 50); }}
                className={`px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap ${darkMode ? 'border-red-600 text-red-400 hover:bg-red-900/30' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
              >
                ✕ Limpar
              </button>
            )}

            {/* Export CSV */}
            <button onClick={exportCSV}
              className={`p-2 rounded-lg border ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}
              title="Exportar CSV"
            >
              <Download className="w-4 h-4" />
            </button>

            {/* View Switcher */}
            <div className={`ml-auto flex rounded-lg border flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {[
                { key: 'kanban', icon: LayoutGrid, title: 'Kanban' },
                { key: 'table', icon: List, title: 'Tabela' },
              ].map(({ key, icon: Icon, title }, i) => (
                <button key={key}
                  onClick={() => { setViewMode(key); localStorage.setItem('campaigns_viewMode', key); }}
                  className={`p-2 transition ${i === 0 ? 'rounded-l-lg' : 'rounded-r-lg'} ${
                    viewMode === key
                      ? 'bg-nomoz-navy text-white'
                      : darkMode ? 'text-gray-400 hover:bg-zinc-700' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  title={title}>
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Kanban Board */}
        {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 min-h-[400px] w-full">
          {/* Coluna: Rascunho / Preparando */}
          <KanbanColumn
            title="📝 Rascunho"
            subtitle="Campanhas em preparação"
            color="yellow"
            darkMode={darkMode}
            onAdd={isOperador() ? () => navigate('/marketing/campanhas/nova') : undefined}
            canEdit={isOperador()}
            canDelete={isGerente()}
            campaigns={filteredCampaigns.filter(c =>
              c.status && !c.status_execucao && (!c.status_campanha || c.status_campanha === 'rascunho')
            )}
            groups={groups}
            fluxos={fluxos}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMarkReady={handleMarkReady}
            navigate={navigate}
            formatarData={formatarData}
            formatarDataHora={formatarDataHora}
          />

          {/* Coluna: Prontas */}
          <KanbanColumn
            title="✅ Prontas"
            subtitle="Aguardando execução ou agendamento"
            color="blue"
            darkMode={darkMode}
            canEdit={isOperador()}
            canDelete={isGerente()}
            campaigns={filteredCampaigns.filter(c =>
              c.status && (!c.status_execucao || c.status_execucao === 'pausada') && c.status_campanha === 'pronta'
            )}
            groups={groups}
            fluxos={fluxos}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMarkReady={handleMarkReady}
            navigate={navigate}
            formatarData={formatarData}
            formatarDataHora={formatarDataHora}
          />

          {/* Coluna: Em execução */}
          <KanbanColumn
            title="🚀 Em execução"
            subtitle="Campanhas disparando agora"
            color="green"
            darkMode={darkMode}
            canEdit={isOperador()}
            canDelete={isGerente()}
            campaigns={filteredCampaigns.filter(c =>
              c.status_execucao === 'executando' && c.status
            )}
            groups={groups}
            fluxos={fluxos}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMarkReady={handleMarkReady}
            navigate={navigate}
            formatarData={formatarData}
            formatarDataHora={formatarDataHora}
          />

          {/* Coluna: Concluídas / Inativas */}
          <KanbanColumn
            title="📦 Concluídas"
            subtitle="Campanhas finalizadas"
            color="gray"
            darkMode={darkMode}
            canEdit={isOperador()}
            canDelete={isGerente()}
            campaigns={filteredCampaigns.filter(c =>
              ['completa', 'incompleta', 'erro', 'cancelada'].includes(c.status_execucao)
            )}
            groups={groups}
            fluxos={fluxos}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMarkReady={handleMarkReady}
            navigate={navigate}
            formatarData={formatarData}
            formatarDataHora={formatarDataHora}
          />
        </div>
        )}

        {/* Tabela */}
        {viewMode === 'table' && (
          <div className="overflow-x-auto">
            <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-gray-700 bg-zinc-800' : 'border-gray-200 bg-white'}`}>
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className={darkMode ? 'bg-zinc-800 border-b border-gray-700' : 'bg-white border-b border-gray-200'}>
                    {['Nome', 'Status', 'Tipo', 'Enviados', 'Entregues', 'Lidos', 'Criador', 'Data'].map(h => (
                      <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {filteredCampaigns.map(campaign => {
                    const statusLabel = campaign.status_execucao === 'executando' ? '🔄 Disparando'
                      : campaign.status_execucao === 'completa' ? '✅ Concluída'
                      : campaign.status_execucao === 'incompleta' ? '⚠️ Incompleta'
                      : campaign.status_execucao === 'erro' ? '❌ Erro'
                      : campaign.status_campanha === 'pronta' ? '🟢 Pronta'
                      : '📝 Rascunho';
                    return (
                      <tr key={campaign.id}
                        onClick={() => navigate(`/marketing/campanhas/${campaign.id}`)}
                        className={`cursor-pointer transition-colors ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'}`}>
                        <td className={`px-4 py-3 max-w-[200px] ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          <div className="font-medium text-sm truncate" title={campaign.nome || 'Sem nome'}>{campaign.nome || 'Sem nome'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium`}>{statusLabel}</span>
                        </td>
                        <td className={`px-4 py-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {campaign.tipo_campanha === 'grupo_whatsapp' ? `📢 ${campaign.total_contatos || 0} grupos` : '👤 Contatos'}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {campaign.total_enviados || 0}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-green-500">
                          {campaign.total_entregues || 0}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>
                          {campaign.total_lidos || 0}
                        </td>
                        <td className={`px-4 py-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {campaign.usuario?.nome?.split(' ')[0] || '—'}
                        </td>
                        <td className={`px-4 py-3 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {formatarData(campaign.create_at)}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredCampaigns.length === 0 && (
                    <tr>
                      <td colSpan={8} className={`px-4 py-8 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Nenhuma campanha encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Campaign Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 transition-opacity">
            <div className={`relative top-10 mx-auto p-6 w-full max-w-2xl shadow-lg rounded-lg ${
              darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"
            }`}>
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-xl font-semibold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}>
                    {editingCampaign ? "Editar Campanha" : "Nova Campanha"}
                  </h3>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setEditingCampaign(null);
                      setFormData({
                        nome: "",
                        descricao: "",
                        tipo: "manual",
                        scheduled_date: "",
                        target_type: "grupo",
                        target_id: "",
                        status: true,
                        fluxo_id: "",
                        tempo_expiracao_fluxo: 24,
                        template_id: "",
                      });
                    }}
                    className={darkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-600"}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Informações Básicas */}
                  <div className={`p-4 rounded-lg ${
                    darkMode ? "bg-zinc-700" : "bg-gray-50"
                  }`}>
                    <h4 className={`text-lg font-medium mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}>
                      Informações Básicas
                    </h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}>
                          Nome da Campanha *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Ex: Promoção Black Friday"
                          className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                            darkMode
                              ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                              : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                          }`}
                          value={formData.nome}
                          onChange={(e) =>
                            setFormData({ ...formData, nome: e.target.value })
                          }
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}>
                          Descrição
                        </label>
                        <textarea
                          placeholder="Descreva o objetivo desta campanha..."
                          className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                            darkMode
                              ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                              : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                          }`}
                          rows={3}
                          value={formData.descricao}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              descricao: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Instância WhatsApp */}
                  <div className={`p-4 rounded-lg ${
                    darkMode ? "bg-zinc-700" : "bg-gray-50"
                  }`}>
                    <h4 className={`text-lg font-medium mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}>
                      Número WhatsApp
                    </h4>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}>
                        Selecione o número *
                      </label>
                      <select
                        className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                          darkMode
                            ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                            : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                        }`}
                        value={formData.instancia_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            instancia_id: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Selecione uma instância</option>
                        {instances.map((instance) => (
                          <option key={instance.id} value={instance.id}>
                            {instance.nome_instancia} - {instance.numero || instance.phone_number_id} ({instance.provider === 'z-api' ? 'Z-API' : 'Meta API'})
                          </option>
                        ))}
                      </select>
                      {instances.length === 0 && (
                        <p className={`mt-1 text-xs ${darkMode ? "text-red-400" : "text-red-500"}`}>
                          Nenhuma instância conectada. Configure uma instância primeiro.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Template */}
                  {!editingCampaign && instances.find(i => i.id === formData.instancia_id)?.provider === 'meta-api' && (
                    <div className={`p-4 rounded-lg ${
                      darkMode ? "bg-zinc-700" : "bg-gray-50"
                    }`}>
                      <h4 className={`text-lg font-medium mb-4 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}>
                        Template (opcional)
                      </h4>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}>
                          Usar template aprovado
                        </label>
                        <select
                          className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                            darkMode
                              ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                              : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                          }`}
                          value={formData.template_id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              template_id: e.target.value,
                            })
                          }
                        >
                          <option value="">Criar nova mensagem</option>
                          {(() => {
                            const instanciaSelecionada = instances.find(i => i.id === formData.instancia_id);
                            const templatesDisponiveis = templates.filter(t => 
                              t.status === 'aprovado' && t.instancia_id === formData.instancia_id
                              || t.instancias?.some(ti => (ti.instancia_id || ti.instancia?.id) === formData.instancia_id && ti.status === 'aprovado')
                            );
                            
                            if (templatesDisponiveis.length === 0) {
                              return (
                                <option value="" disabled>
                                  {formData.instancia_id 
                                    ? 'Nenhum template aprovado para esta instância'
                                    : 'Selecione uma instância primeiro'
                                  }
                                </option>
                              );
                            }
                            
                            return templatesDisponiveis.map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.nome}
                              </option>
                            ));
                          })()}
                        </select>
                        <p className={`mt-1 text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                          Templates são específicos por instância/WABA. Crie templates em "Templates".
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Configurações de Envio */}
                  <div className={`p-4 rounded-lg ${
                    darkMode ? "bg-zinc-700" : "bg-gray-50"
                  }`}>
                    <h4 className={`text-lg font-medium mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}>
                      Configurações de Envio
                    </h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}>
                          Tipo de Execução
                        </label>
                        <div className="space-y-2">
                          {[
                            {
                              value: "manual",
                              label: "Manual",
                              desc: "Executar quando necessário",
                            },
                            {
                              value: "agendada",
                              label: "Agendada",
                              desc: "Executar em data específica",
                            },
                            {
                              value: "por_acao",
                              label: "Por Ação",
                              desc: "Executar por gatilho",
                            },
                          ].map((option) => (
                            <label
                              key={option.value}
                              className="flex items-start space-x-3 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="tipo"
                                value={option.value}
                                checked={formData.tipo === option.value}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    tipo: e.target.value,
                                  })
                                }
                                className={`mt-1 h-4 w-4 focus:ring-nomoz-orange ${
                                  darkMode
                                    ? "text-green-600 border-gray-600 bg-zinc-600"
                                    : "text-green-600 border-gray-300"
                                }`}
                              />
                              <div>
                                <div className={`text-sm font-medium ${
                                  darkMode ? "text-white" : "text-gray-900"
                                }`}>
                                  {option.label}
                                </div>
                                <div className={`text-xs ${
                                  darkMode ? "text-gray-400" : "text-gray-500"
                                }`}>
                                  {option.desc}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {formData.tipo === "agendada" && (
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}>
                            Data e Hora do Agendamento
                          </label>
                          <input
                            type="datetime-local"
                            required
                            min={(() => {
                              const now = new Date();
                              now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
                              return now.toISOString().slice(0, 16);
                            })()}
                            className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                              darkMode
                                ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                                : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                            }`}
                            value={formData.scheduled_date}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                scheduled_date: e.target.value,
                              })
                            }
                          />
                        </div>
                      )}

                      {/* Fluxo Específico da Campanha */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}>
                          Fluxo de Conversa (opcional)
                        </label>
                        <select
                          className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                            darkMode
                              ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                              : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                          }`}
                          value={formData.fluxo_id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              fluxo_id: e.target.value,
                            })
                          }
                        >
                          <option value="">Sem fluxo específico (usa o padrão)</option>
                          {Array.isArray(fluxos) && fluxos.map((fluxo) => (
                            <option key={fluxo.id} value={fluxo.id}>
                              {fluxo.nome} {fluxo.padrao ? '(Padrão)' : ''}
                            </option>
                          ))}
                        </select>
                        <p className={`mt-1 text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                          Se definido, usuários que responderem entrarão neste fluxo
                        </p>
                      </div>

                      {formData.fluxo_id && (
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}>
                            Tempo de Expiração (horas)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="720"
                            className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                              darkMode
                                ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                                : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                            }`}
                            value={formData.tempo_expiracao_fluxo}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                tempo_expiracao_fluxo: parseInt(e.target.value) || 24,
                              })
                            }
                          />
                          <p className={`mt-1 text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                            Tempo que o usuário tem para responder e entrar no fluxo (padrão: 24h)
                          </p>
                        </div>
                      )}
                    </div>
                    

                  </div>

                  {/* Destinatários */}
                  <div className={`p-4 rounded-lg ${
                    darkMode ? "bg-zinc-700" : "bg-gray-50"
                  }`}>
                    <h4 className={`text-lg font-medium mb-4 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}>
                      Destinatários
                    </h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}>
                          Enviar para
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="target_type"
                              value="grupo"
                              checked={formData.target_type === "grupo"}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  target_type: e.target.value,
                                  target_id: "",
                                })
                              }
                              className={`h-4 w-4 focus:ring-nomoz-orange ${
                                darkMode
                                  ? "text-green-600 border-gray-600 bg-zinc-600"
                                  : "text-green-600 border-gray-300"
                              }`}
                            />
                            <div>
                              <div className={`text-sm font-medium ${
                                darkMode ? "text-white" : "text-gray-900"
                              }`}>
                                Grupo de Contatos
                              </div>
                              <div className={`text-xs ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}>
                                Enviar para todos os contatos de um grupo
                              </div>
                            </div>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="target_type"
                              value="contato"
                              checked={formData.target_type === "contato"}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  target_type: e.target.value,
                                  target_id: "",
                                })
                              }
                              className={`h-4 w-4 focus:ring-nomoz-orange ${
                                darkMode
                                  ? "text-green-600 border-gray-600 bg-zinc-600"
                                  : "text-green-600 border-gray-300"
                              }`}
                            />
                            <div>
                              <div className={`text-sm font-medium ${
                                darkMode ? "text-white" : "text-gray-900"
                              }`}>
                                Contato Individual
                              </div>
                              <div className={`text-xs ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}>
                                Enviar para um contato específico
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}>
                          {formData.target_type === "grupo"
                            ? "Selecionar Grupo"
                            : "Selecionar Contato"}
                        </label>

                        <div className="relative">
                          <select
                            required
                            className={`appearance-none block w-full rounded-md shadow-sm py-2 px-3 pr-10 ${
                              darkMode
                                ? "bg-zinc-600 border border-gray-700 text-white focus:ring-nomoz-orange focus:border-nomoz-orange"
                                : "bg-white border border-gray-300 text-gray-900 focus:ring-nomoz-orange focus:border-nomoz-orange"
                            }`}
                            value={formData.target_id}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                target_id: e.target.value,
                              })
                            }
                          >
                            <option value="">Selecione...</option>
                            {formData.target_type === "grupo"
                              ? Array.isArray(groups) &&
                                groups.map((group) => (
                                  <option key={group.id} value={group.id}>
                                    {group.nome} ({group.totalContatos || 0}{" "}
                                    contatos)
                                  </option>
                                ))
                              : Array.isArray(contacts) &&
                                contacts.map((contact) => (
                                  <option key={contact.id} value={contact.id}>
                                    {contact.nome} - {contact.telefone}
                                  </option>
                                ))}
                          </select>

                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <svg
                              className={`h-4 w-4 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>

                        {formData.target_id && (
                          <div className={`mt-2 p-2 rounded border ${
                            darkMode ? "bg-zinc-600 border-gray-700" : "bg-gray-50 border-gray-200"
                          }`}>
                            <div className={`text-xs ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`}>
                              {formData.target_type === "grupo" ? (
                                <>
                                  📊 Grupo selecionado com{" "}
                                  {groups?.find(
                                    (g) => g.id === formData.target_id
                                  )?.contatos?.length || 0}{" "}
                                  contatos
                                </>
                              ) : (
                                <>👤 Contato individual selecionado</>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    darkMode ? "bg-zinc-700" : "bg-gray-50"
                  }`}>
                    <div>
                      <h4 className={`text-sm font-medium ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}>
                        Status da Campanha
                      </h4>
                      <p className={`text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}>
                        Campanhas ativas podem ser executadas
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className={`h-4 w-4 focus:ring-nomoz-orange ${
                          darkMode
                            ? "text-green-600 border-gray-600 bg-zinc-600"
                            : "text-green-600 border-gray-300"
                        }`}
                        checked={formData.status}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.checked })
                        }
                      />
                      <span className={`ml-2 text-sm ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}>
                        {formData.status ? "Ativa" : "Inativa"}
                      </span>
                    </label>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingCampaign(null);
                        setFormData({
                          nome: "",
                          descricao: "",
                          tipo: "manual",
                          scheduled_date: "",
                          target_type: "grupo",
                          target_id: "",
                          status: true,
                        });
                      }}
                      className={`px-6 py-3 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange ${
                        darkMode
                          ? "border-gray-600 text-gray-300 bg-zinc-700 hover:bg-zinc-600"
                          : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="gradient-button px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange"
                    >
                      {editingCampaign
                        ? "Atualizar Campanha"
                        : "Criar Campanha"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const statusLabels = {
  rascunho: { label: 'Rascunho', bg: 'bg-gray-100 text-gray-700', bgDark: 'bg-gray-700 text-gray-300' },
  submetido: { label: 'Em avaliação', bg: 'bg-yellow-100 text-yellow-700', bgDark: 'bg-yellow-900/20 text-yellow-400' },
  rejeitado: { label: 'Rejeitado', bg: 'bg-red-100 text-red-700', bgDark: 'bg-red-900/20 text-red-400' },
  aprovado: { label: 'Aprovado', bg: 'bg-green-100 text-green-700', bgDark: 'bg-green-900/20 text-green-400' },
  disparando: { label: 'Disparando', bg: 'bg-blue-100 text-blue-700', bgDark: 'bg-blue-900/20 text-blue-400' },
  concluido: { label: 'Concluído', bg: 'bg-purple-100 text-purple-700', bgDark: 'bg-purple-900/20 text-purple-400' },
};

const columnColors = {
  yellow: { header: 'border-yellow-500', count: 'bg-yellow-100 text-yellow-700', countDark: 'bg-yellow-900/20 text-yellow-400' },
  blue: { header: 'border-blue-500', count: 'bg-blue-100 text-blue-700', countDark: 'bg-blue-900/20 text-blue-400' },
  green: { header: 'border-nomoz-navy', count: 'bg-green-100 text-green-700', countDark: 'bg-green-900/20 text-green-400' },
  gray: { header: 'border-gray-500', count: 'bg-gray-100 text-gray-700', countDark: 'bg-gray-700 text-gray-300' },
};

const KanbanCard = ({ campaign, darkMode, groups, fluxos, onEdit, onDelete, onMarkReady, navigate, formatarData, formatarDataHora, canEdit = true, canDelete = true }) => {
  const execLabels = {
    executando: { label: '🔄 Disparando', bg: 'bg-blue-100 text-blue-700', bgDark: 'bg-blue-900/20 text-blue-400' },
    pausada: { label: '⏸️ Pausada', bg: 'bg-yellow-100 text-yellow-700', bgDark: 'bg-yellow-900/20 text-yellow-400' },
    completa: { label: '✅ Concluída', bg: 'bg-green-100 text-green-700', bgDark: 'bg-green-900/20 text-green-400' },
    incompleta: { label: '⚠️ Incompleta', bg: 'bg-yellow-100 text-yellow-700', bgDark: 'bg-yellow-900/20 text-yellow-400' },
    cancelada: { label: '🛑 Cancelada', bg: 'bg-red-100 text-red-700', bgDark: 'bg-red-900/20 text-red-400' },
    erro: { label: '❌ Erro', bg: 'bg-red-100 text-red-700', bgDark: 'bg-red-900/20 text-red-400' },
  };

  const badge = campaign.status_execucao
    ? execLabels[campaign.status_execucao] || null
    : null;
  const totalEnviados = campaign.total_enviados || 0;
  const totalContatos = campaign.total_contatos || 0;
  const progresso = totalContatos > 0 ? Math.round((totalEnviados / totalContatos) * 100) : 0;

  return (
    <div className={`p-4 rounded-lg border transition-all hover:shadow-md ${darkMode ? 'bg-zinc-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <h4 className={`font-semibold text-sm truncate flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {campaign.nome || 'Sem nome'}
        </h4>
        {badge && (
        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${darkMode ? badge.bgDark : badge.bg}`}>
          {badge.label}
        </span>
        )}
      </div>

      {campaign.descricao && (
        <p className={`text-xs mb-3 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {campaign.descricao}
        </p>
      )}

      {/* Info */}
      <div className="space-y-1.5 mb-3">
        <div className={`flex justify-between text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>{campaign.tipo === 'agendada' ? '📅 Agendada' : '✋ Manual'}</span>
          <span>{campaign.tipo_campanha === 'grupo_whatsapp' ? `📢 ${campaign.total_contatos || 0} grupos` : campaign.tipo_origem === 'grupo' ? `👥 ${groups.find(g => g.id === campaign.grupo_id)?.nome || 'Grupo'}` : '👤 Contato'}</span>
        </div>
        {campaign.scheduled_date && (
          <p className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
            🕐 {formatarDataHora(campaign.scheduled_date)}
          </p>
        )}
        {campaign.fluxo_id && (
          <p className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            🤖 {fluxos.find(f => f.id === campaign.fluxo_id)?.nome || 'Fluxo'}
          </p>
        )}
      </div>

      {/* Progresso de envio */}
      {totalContatos > 0 && (
        <div className="mb-3">
          {['completa', 'incompleta'].includes(campaign.status_execucao) ? (
            // Campanha finalizada: mostrar entregues e falhas
            <div className="flex gap-2 text-xs">
              <span className="text-green-500">✅ {campaign.total_entregues || 0} entregues</span>
              {(campaign.total_falhas || 0) > 0 && (
                <span className="text-red-500">❌ {campaign.total_falhas} falhas</span>
              )}
              {(campaign.total_lidos || 0) > 0 && (
                <span className={darkMode ? 'text-blue-400' : 'text-blue-500'}>👁 {campaign.total_lidos} lidos</span>
              )}
            </div>
          ) : (
            // Em andamento: mostrar progresso
            <>
              <div className="flex justify-between text-xs mb-1">
                <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{totalEnviados}/{totalContatos} enviados</span>
                <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{progresso}%</span>
              </div>
              <div className={`w-full h-1.5 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                <div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${progresso}%` }} />
              </div>
            </>
          )}
        </div>
      )}

      {/* Flag de template — só em campanhas não concluídas e que NÃO são de grupo */}
      {!['completa', 'incompleta'].includes(campaign.status_execucao) && campaign.tipo_campanha !== 'grupo_whatsapp' && (
        <div className={`text-xs px-2.5 py-1.5 rounded-lg mb-3 ${
          campaign.template_status === 'rascunho' ? (darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500') :
          campaign.template_status === 'submetido' ? (darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-50 text-yellow-600') :
          campaign.template_status === 'aprovado' ? (darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-600') :
          campaign.template_status === 'rejeitado' ? (darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600') :
          (darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-600')
        }`}>
          {campaign.template_status === 'rascunho' && !campaign.template_id && '📝 Template não enviado'}
          {campaign.template_status === 'submetido' && '⏳ Aguardando aprovação'}
          {(campaign.template_status === 'aprovado' || campaign.template_id) && '✅ Template aprovado'}
          {campaign.template_status === 'rejeitado' && '❌ Template rejeitado'}
          {campaign.template_status === 'disparando' && '🚀 Disparando'}
          {campaign.template_status === 'concluido' && '✔️ Concluído'}
        </div>
      )}
      {!['completa', 'incompleta'].includes(campaign.status_execucao) && campaign.template_id && !campaign.template_meta_id && !campaign.template_status && (
        <div className={`text-xs px-2.5 py-1.5 rounded-lg mb-3 ${darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-600'}`}>
          ✅ Template aprovado
        </div>
      )}

      {/* Rejeição */}
      {campaign.template_status === 'rejeitado' && campaign.template_rejection_reason && (
        <div className={`text-xs p-2 rounded mb-3 ${darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
          ⚠️ {campaign.template_rejection_reason}
        </div>
      )}

      {/* Footer */}
      <div className={`flex items-center justify-between pt-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            {formatarData(campaign.create_at || Date.now())}
          </span>
          {campaign.usuario?.nome && (
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              • {campaign.usuario.nome.split(' ')[0]}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Botão marcar como pronta — só se campanha pode rodar */}
          {(!campaign.status_campanha || campaign.status_campanha === 'rascunho') && !campaign.status_execucao && 
           (campaign.tipo_campanha === 'grupo_whatsapp' || campaign.template_status === 'aprovado') && (
            <button onClick={() => onMarkReady(campaign.id)}
              className={`px-2 py-1 rounded text-xs font-medium ${darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`} title="Marcar como pronta">
              ✓ Pronta
            </button>
          )}
          <button onClick={() => navigate(`/marketing/campanhas/${campaign.id}`)}
            className={`p-1.5 rounded text-xs ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} title="Detalhes">
            <FileText className="w-3.5 h-3.5" />
          </button>
          {!['completa', 'incompleta', 'executando'].includes(campaign.status_execucao) && (
            <>
              {canEdit && (
                <button onClick={() => onEdit(campaign)}
                  className={`p-1.5 rounded text-xs ${darkMode ? 'hover:bg-zinc-700 text-blue-400' : 'hover:bg-blue-50 text-blue-500'}`} title="Editar">
                  <Edit className="w-3.5 h-3.5" />
                </button>
              )}
              {canDelete && (
                <button onClick={() => onDelete(campaign.id)}
                  className={`p-1.5 rounded text-xs ${darkMode ? 'hover:bg-zinc-700 text-red-400' : 'hover:bg-red-50 text-red-500'}`} title="Excluir">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const KanbanColumn = ({ title, subtitle, color, darkMode, campaigns, groups, fluxos, onEdit, onDelete, onMarkReady, navigate, formatarData, formatarDataHora, onAdd, canEdit = true, canDelete = true }) => {
  const cc = columnColors[color];
  return (
    <div className={`flex flex-col rounded-xl border-t-4 ${cc.header} ${darkMode ? 'bg-zinc-800/50 border-x border-b border-gray-700' : 'bg-gray-50 border-x border-b border-gray-200'}`}>
      {/* Header da coluna */}
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
          <div className="flex items-center gap-2">
            {onAdd && (
              <button onClick={onAdd} className={`p-1 rounded-md transition-colors ${darkMode ? 'hover:bg-zinc-700 text-green-400' : 'hover:bg-green-50 text-green-600'}`} title="Nova campanha">
                <Plus className="w-4 h-4" />
              </button>
            )}
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${darkMode ? cc.countDark : cc.count}`}>
              {campaigns.length}
            </span>
          </div>
        </div>
        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{subtitle}</p>
      </div>

      {/* Cards */}
      <div className="flex-1 px-3 pb-3 space-y-3">
        {campaigns.length === 0 ? (
          <div className={`text-center py-8 text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            Nenhuma campanha
          </div>
        ) : (
          campaigns.map(c => (
            <KanbanCard key={c.id} campaign={c} darkMode={darkMode} groups={groups} fluxos={fluxos}
              onEdit={onEdit} onDelete={onDelete} onMarkReady={onMarkReady} navigate={navigate}
              formatarData={formatarData} formatarDataHora={formatarDataHora}
              canEdit={canEdit} canDelete={canDelete} />
          ))
        )}
      </div>
    </div>
  );
};

export default Campaigns;